/**
 * Global-header trigger for switching identity into a VAU (and back).
 *
 * OPAL-5567 moved this out of the left nav: the switcher now sits in the
 * top bar beside the notification bell / Tracker toggle, injected through
 * the same `slot="system-component"` those buttons use. That slot is the
 * only part of `opti-common-header` Opal renders into — the `?` help icon
 * the ticket positions us against lives *inside* the Stencil component and
 * is not ours to order against, so "left of help" holds only as far as the
 * slot itself sits left of it.
 *
 * `IconArrowRightArrowLeft` is a stand-in. The ticket has a dedicated
 * switch-account glyph pending from the design-system team; swapping it is
 * a one-line change here.
 *
 * The menu lists the principal's VAUs as avatar + name. Deliberately no
 * role byline: the context endpoint returns `{vau_id, vau_name}` only
 * (`vau_context_service._get_available_vaus`), and the avatar is initials
 * derived from the name — the same thing IM does for VAUs in
 * `ConversationAvatar` / `SenderAvatar`, so the two surfaces render the
 * same person the same way without a new API field.
 */

import { IconArrowRightArrowLeft } from '@optiaxiom/icons';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Menu,
  MenuContent,
  type MenuOption,
  MenuTrigger,
  Spinner,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import {
  useAssumeVAU,
  useReleaseVAU,
  useVAUContext,
} from '@frontend/features/vau/hooks/useVAUContext';

export const VauSwitchButton = () => {
  // Gated by the `vau` feature flag only during rollout.
  // TODO: re-add canInstance(['vau_enabled']) check once provisioned in Turnstile.
  const [vauDecision] = useDecision('vau');
  const vauFlag = vauDecision?.enabled === true;

  const { data } = useVAUContext();
  const assume = useAssumeVAU();
  const release = useReleaseVAU();

  if (!vauFlag || !data) return null;

  const isAssumed = data.is_assumed;
  const available = data.available_vaus;

  // Nothing to offer and we're not in VAU mode → don't take a header slot.
  if (!isAssumed && available.length === 0) return null;

  const isSwitching = assume.isPending || release.isPending;
  const switchingMessage = release.isPending
    ? 'Switching back to your profile…'
    : `Switching to ${available.find(v => v.vau_id === assume.variables)?.vau_name ?? 'VAU'}…`;

  const options: MenuOption[] = [];
  if (isAssumed) {
    options.push({
      label: 'Switch back to my profile',
      execute: () => release.mutate({}),
    });
  }
  for (const v of available) {
    options.push({
      label: v.vau_name,
      // Initials avatar, matching how IM renders a VAU. `addon` is the
      // Menu's slot for leading content.
      addon: <Avatar name={v.vau_name} size="sm" />,
      // The currently assumed VAU is shown but disabled to make the
      // state explicit; clicking it would be a no-op.
      disabledReason:
        isAssumed && data.vau_id === v.vau_id ? 'Already acting as this VAU' : undefined,
      execute: () => assume.mutate(v.vau_id),
    });
  }

  const label = isAssumed ? `Acting as ${data.vau_name ?? 'a VAU'}` : 'Switch identity';

  return (
    <>
      <Menu options={options}>
        <Tooltip content={label} delayDuration={0}>
          <MenuTrigger asChild>
            <Button
              appearance="subtle"
              aria-label={label}
              disabled={isSwitching}
              icon={<IconArrowRightArrowLeft />}
              // Matches the bell and Tracker toggle beside it — the ticket
              // calls out that the buttons must be the same size.
              size="lg"
              data-testid="vau-switch-trigger"
            />
          </MenuTrigger>
        </Tooltip>
        <MenuContent />
      </Menu>
      {isSwitching ? (
        // Full-viewport scrim while the identity swap is in flight.
        // Axiom Box exposes a `z` sprinkle (allows `toast`/`tooltip`
        // tokens) but does NOT expose `position`/`top`/`left`/`right`/
        // `bottom` as sprinkles — passing them would be silently dropped
        // and the scrim would render in normal flow inside the header.
        // Same goes for arbitrary `backgroundColor` values. All five
        // layout properties + the dim colour are therefore set via
        // `style` so they actually reach the DOM.
        <Box
          z="toast"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 15, 20, 0.55)',
          }}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            h="full"
            gap="16"
          >
            <Spinner size="xl" />
            <Text fontSize="lg" color="fg.default">
              {switchingMessage}
            </Text>
          </Flex>
        </Box>
      ) : null}
    </>
  );
};
